import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getSite, isValidSiteSha, getSiteSaveReadiness } from "./api";

export { isValidSiteSha, getSiteSaveReadiness };

// Shown until the API responds (and as a fallback if it fails) — mirrors the
// backend defaults so the page never renders empty.
export const DEFAULT_SITE = {
  bio: "I am a high school student immersing myself in cybersecurity under the handle oVu-Lab. I avoid inflated credentials and focus on code, packet captures, and defensive strategies that stand up to review.",
  toolset: "Python, Bash, Wireshark, Linux",
  goal_text: "College CS & Cyber Operations",
  github_url: "https://github.com/oVu-Lab",
  studying: ["Linux", "Networking", "Web Security", "Python", "CTFs"],
  horizon: ["AI Security", "Vibe Coding"],
  goals: [
    "Build a small portfolio of real, reviewable security tools",
    "Practice on CTF platforms and document what each challenge taught me",
    "Get comfortable administering and hardening my own Linux lab",
    "Study computer science and cybersecurity at college",
  ],
};

// "https://github.com/oVu-Lab" -> "oVu-Lab"
export function githubHandle(url) {
  const part = (url || "").replace(/\/+$/, "").split("/").pop();
  return part || "oVu-Lab";
}

const SiteContext = createContext({
  site: DEFAULT_SITE,
  loading: true,
  loadError: "",
  ready: false,
  reload: () => Promise.resolve(null),
});

export function SiteProvider({ children }) {
  const [site, setSite] = useState(DEFAULT_SITE);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const reload = useCallback(() => {
    setLoading(true);
    setLoadError("");
    return getSite()
      .then((data) => {
        if (!data || typeof data !== "object") {
          setLoadError("Invalid site settings response from the server.");
          return null;
        }
        setSite(data);
        if (!isValidSiteSha(data.content_sha)) {
          setLoadError(
            "Site settings loaded without a valid version SHA (content_sha). Saving is disabled until content/site.json is initialized on the server."
          );
        }
        return data;
      })
      .catch(() => {
        setLoadError("Could not load site settings from the server. Retry before editing.");
        return null;
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  useEffect(() => { reload(); }, [reload]);
  const { ready } = getSiteSaveReadiness({ site, loading, loadError });
  return (
    <SiteContext.Provider value={{ site, loading, loadError, ready, reload }}>
      {children}
    </SiteContext.Provider>
  );
}

export function useSite() {
  return useContext(SiteContext);
}
